import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import UserHeader, { transitionDuration } from "./UserHeader";
import { useUserContext } from "../Context/UserContext";
import { getAllUsers, deleteUser } from "../api";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Estilos do Toastify
import FilterBar from "./FilterBar";
import AdminToolbar from "./AdminToolbar";
import OptionsMenu from "./OptionsMenu";
import ManagerEntitiesMenu from "./ManagerEntitiesMenu";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditPermissionsModal from "./EditPermissionsModal";
import ManagerUserModal from "./ManagerUserModal";
import { getUserByLogin } from "../api";
import { useNavigate } from "react-router-dom";


const Admin: React.FC = () => {
  const navigate = useNavigate();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState<"light" | "dark">("light");
  const { setCurrentPath } = useUserContext();
  const [fullName, setFullName] = useState<string>("Usuário");
  const [login, setLogin] = useState<string>("admin");
  const [userType, setUserType] = useState<number | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | null>(null); // Armazena o nome do usuário para confirmação
  const [isLoginLoaded, setIsLoginLoaded] = useState(false); // Flag de carregamento
  const [originalUsers, setOriginalUsers] = useState<any[]>([]); // Guarda a lista original
  const [statusMessage, setStatusMessage] = useState<string>(""); // Para gerenciar mensagens de status
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<any>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({ key: "", direction: null });
  const [isDeleting, setIsDeleting] = useState(false); // Novo estado para controlar se está deletando

useEffect(() => {
  const username = sessionStorage.getItem("username");
  const token = sessionStorage.getItem("token");

  if (!token || !username) {
    toast.error("Sessão expirada. Faça login novamente.");
    navigate("/login");
    return;
  }

  const fetchLoggedUser = async () => {
    try {
      const userData = await getUserByLogin(username);
      if (!userData) {
        toast.error("Usuário não encontrado. Faça login novamente.");
        navigate("/login");
        return;
      }

      // 🔐 Checa o tipo real retornado do backend
      if (userData.type !== 99) {
        toast.error("Acesso negado. Apenas administradores podem acessar.");
        navigate("/user");
        return;
      }

      setFullName(userData.fullName || "Usuário");
      setLogin(userData.login);
      setUserType(userData.type);
    } catch (error: any) {
      console.error("Erro ao carregar usuário logado:", error.message);
      toast.error("Sessão inválida. Faça login novamente.");
      sessionStorage.clear();
      navigate("/login");
    } finally {
      setIsLoginLoaded(true);
    }
  };

  fetchLoggedUser();
}, [navigate]);

  useEffect(() => {
    if (isLoginLoaded && login) {
      fetchUsers(login); // Chama o fetchUsers automaticamente quando o login é carregado
    }
  }, [isLoginLoaded, login]);

  const handleOpenPermissionsModal = (user: any) => {
    setPermissionsUser(user);
    setPermissionsModalOpen(true);
  };

  const handleClosePermissionsModal = () => {
    setPermissionsUser(null);
    setPermissionsModalOpen(false);
  };

  // Função de ordenação (não alterada)
  const renderSortArrow = (key: string) => {
    if (sortConfig.key !== key || !sortConfig.direction) return ""; // Sem seta
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const resetSortConfig = () => {
    setSortConfig({ key: "", direction: null });
    const resetUsers = [...originalUsers].sort((a, b) => {
      if (a.type === 99 && b.type !== 99) return -1; // Prioriza administradores
      if (a.type !== 99 && b.type === 99) return 1;
      return 0;
    });
    setUsers(resetUsers);
  };

  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => {
      // Se clicar na mesma coluna pela terceira vez, limpa o filtro
      if (prevConfig.key === key && prevConfig.direction === "desc") {
        resetSortConfig();
        return { key: "", direction: null };
      }

      let newDirection: "asc" | "desc" | null;
      // Alterna entre ascendente, descendente e desativado
      if (prevConfig.key === key) {
        newDirection = prevConfig.direction === "asc" ? "desc" : "asc";
      } else {
        newDirection = "asc";
      }

      return { key, direction: newDirection };
    });
  };

  // Ordenação dos usuários (não alterada)
  useEffect(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      const defaultUsers = [...originalUsers];
      const loggedInUser = defaultUsers.find((user) => user.login === login);
      
      if (loggedInUser) {
        // Remove o usuário logado da lista e o insere como primeiro
        const filtered = defaultUsers.filter((user) => user.login !== login);
        setUsers([loggedInUser, ...filtered]);
      } else {
        setUsers(defaultUsers);
      }
      return;
    }
  
    const sortedUsers = [...users].sort((a, b) => {
      let valueA = a[sortConfig.key];
      let valueB = b[sortConfig.key];
  
      const isNumberA = /^\d+$/.test(valueA);
      const isNumberB = /^\d+$/.test(valueB);
  
      if (isNumberA && !isNumberB)
        return sortConfig.direction === "asc" ? -1 : 1;
      if (!isNumberA && isNumberB)
        return sortConfig.direction === "asc" ? 1 : -1;
  
      if (typeof valueA !== "string") valueA = String(valueA);
      if (typeof valueB !== "string") valueB = String(valueB);
  
      return sortConfig.direction === "asc"
        ? valueA.localeCompare(valueB, undefined, { numeric: true })
        : valueB.localeCompare(valueA, undefined, { numeric: true });
    });
  
    const loggedInUser = sortedUsers.find((user) => user.login === login);
    if (loggedInUser) {
      // Remove o usuário logado da lista e o insere como primeiro
      const filtered = sortedUsers.filter((user) => user.login !== login);
      setUsers([loggedInUser, ...filtered]);
    } else {
      setUsers(sortedUsers);
    }
  }, [sortConfig, originalUsers, login]);
  

  const refreshAdminUsers = async () => {
    try {
      // Atualiza a lista de usuários no backend
      await fetchUsers(login);
    } catch (error) {
      console.error("Erro ao atualizar os usuários:", error);
    }
  };
  
  // Passe a função como prop para o AdminToolbar
  <AdminToolbar onRefreshUsers={refreshAdminUsers} setStatusMessage={setStatusMessage} />
  

  const fetchUsers = async (currentLogin: string) => {
    setLoading(true); // Inicia o estado de carregamento
    setError(null); // Reseta qualquer erro anterior
  
    try {
      const fetchedUsers = await getAllUsers(currentLogin); // API para buscar usuários
      const sortedUsers = fetchedUsers.sort((a: any, b: any) => {
        if (a.type === 99 && b.type !== 99) return -1; // Administradores primeiro
        if (a.type !== 99 && b.type === 99) return 1;
        return a.fullName.localeCompare(b.fullName, undefined, { numeric: true }); // Ordena alfabeticamente pelo nome
      });
      setOriginalUsers(sortedUsers); // Armazena a lista original
      setUsers(sortedUsers); // Atualiza a lista exibida
    } catch (err: any) {
      setError(err.message || "Erro ao buscar usuários."); // Exibe a mensagem de erro, caso ocorra
    } finally {
      setLoading(false); // Finaliza o estado de carregamento
    }
  };
  // Executa apenas uma vez ao montar o componente

  const toggleColorMode = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: "#1976d2" }, // Cor do botão principal
          secondary: { main: "#d32f2f" }, // Cor do botão de exclusão
          background: {
            default: mode === "dark" ? "#545454" : "#cfe3fc",
          },
          text: {
            primary: mode === "dark" ? "#e0f6ff" : "#626262", // Cor consistente para texto
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    const body = document.body;

    body.style.transition = `background-color ${transitionDuration}`;
    body.style.backgroundColor = mode === "dark" ? "#464646" : "#b5d0eb"; // Fundo preto escuro no modo dark e branco no modo claro

    return () => {
      body.style.transition = "0.8s";
    };
  }, [mode]);

  // Função chamada ao abrir o modal de confirmação
  const handleOpenExcludeModal = (user: any) => {
    setSelectedUser(user.login); // Armazena o login do usuário a ser excluído
    setSelectedUserName(user.fullName); // Armazena o nome do usuário
    setModalOpen(true); // Abre o modal
  };

  const handleCloseModal = () => {
    setModalOpen(false); // Fecha o modal sem excluir
    setSelectedUser(null); // Limpa a seleção
    setSelectedUserName(null); // Limpa o nome selecionado
  };

  const handleExcludeUser = async () => {
    if (!selectedUser || isDeleting) return;

    setIsDeleting(true);

    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) return;

    const user = JSON.parse(storedUser);

    try {
      const response = await deleteUser(user.login, selectedUser);

      toast.success(`Usuário ${selectedUserName} excluído com sucesso!`, {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error: any) {
      console.error("Erro ao deletar o usuário:", error);

      const errorMessage =
        `${error.message} Lista Atualizada.` || // Mensagem do erro capturada no deleteUser
        "Erro desconhecido ao excluir usuário."; // Fallback genérico

      toast.error(`${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      // Garante que a lista seja atualizada e o estado seja limpo
      await fetchUsers(user.login); // Atualiza a lista de usuários
      setModalOpen(false);
      setIsDeleting(false);
      setSelectedUser(null);
      setSelectedUserName(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          backgroundColor: mode === "dark" ? "#464646" : "#b5d0eb", // Cor ajustada
          transition: `background-color ${transitionDuration}`,
        }}
      >
        <UserHeader
          toggleColorMode={toggleColorMode}
          fullName={fullName}
          login={login}
          userType={userType ?? 99}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
            padding: "20px",
            transition: `background-color ${transitionDuration}`,
          }}
        >
          <Paper
            sx={{
              padding: "40px", // Mantém o padding
              width: "80vw", // Largura de 80% da viewport
              maxWidth: "1400px", // Define um limite máximo de largura
              margin: "0 auto",
              height: {
                xs: "100%", // Ocupa toda a altura em telas menores
                sm: "90%", // Ajuste responsivo para telas médias
                md: "85%", // Levemente maior para telas médias
                lg: "85%", // Levemente maior para telas grandes
                xl: "85%", // Altura maior para telas muito grandes
              },
              maxHeight: "90vh", // Aumenta a altura máxima para ocupar mais da tela
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              overflowY: "auto",
              transition: `background-color ${transitionDuration}, transform ${transitionDuration}`,
              backgroundColor:
                mode === "dark"
                  ? "#1e1e1e" // Cor mais escura no modo dark
                  : "#d4eaff", // Azul claro no modo claro
              boxShadow:
                mode === "dark"
                  ? "0px 4px 10px rgba(0, 0, 0, 0.9)" // Sombra mais intensa no dark
                  : "0px 4px 10px rgba(0, 0, 0, 0.6)", // Sombra mais leve no claro
            }}
          >
            <Typography variant="h4" gutterBottom>
              Cadastro de Usuários
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between", // Alinha os filhos para as extremidades
                alignItems: "center",
                gap: 2,
                width: "100%",
                backgroundColor: mode === "dark" ? "#3c3c3c" : "#d4f4ff",
                padding: "8px 16px",
                borderRadius: "4px",
                boxShadow:
                  mode === "dark"
                    ? "0px 4px 8px rgba(0, 0, 0, 0.5)"
                    : "0px 4px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: "16px",
                transition: `background-color ${transitionDuration}`,
                flexWrap: "wrap", // Permite quebrar em telas pequenas
              }}
            >
              {/* Box da esquerda: FilterBar */}
              <Box>
                <FilterBar
                  onFilterChange={(field, value) => {
                    const filteredUsers = originalUsers.filter((user) =>
                      user[field]?.toLowerCase().includes(value.toLowerCase())
                    );
                    setUsers(filteredUsers);
                  }}
                />
              </Box>

              {/* Box da direita: AdminToolbar */}
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "flex-end", // Controla alinhamento
                  gap: 1, // Espaçamento entre os elementos
                  marginRight: "10px", // Move o grupo para a esquerda
                }}
              >
                <AdminToolbar
                  onRefreshUsers={() => fetchUsers(login)}
                  setStatusMessage={setStatusMessage}
                />
                <DragIndicatorIcon
                  sx={{
                    color: theme.palette.text.primary,
                    cursor: "default",
                    "&:hover": { color: theme.palette.text.primary },
                    transition: "background-color 0.8s ease-in-out",
                  }}
                />
                <ManagerEntitiesMenu
                  fetchUsers={fetchUsers} // Passe a função fetchUsers
                  login={login} // Passe o login
                />
                <Box
                  sx={{
                    marginTop: "3px",
                    borderLeft: "1px solid gray",
                    height: "18px",
                  }}
                />
                <OptionsMenu toggleColorMode={toggleColorMode} />
                <DragIndicatorIcon
                  sx={{
                    color: theme.palette.text.primary,
                    cursor: "default",
                    "&:hover": { color: theme.palette.text.primary },
                    transition: "background-color 0.8s ease-in-out",
                  }}
                />
              </Box>
            </Box>

            {loading ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  display: "flex", // Torna o conteúdo flexível
                  flexDirection: "column", // Alinha os filhos verticalmente
                  height: "100%", // Garante altura total
                  overflowY: "auto", // Scroll quando necessário
                  transition: "background-color 0.8s ease-in-out",
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    transition: `background-color ${transitionDuration}, color ${transitionDuration}`, // Transição para tabela
                    "& th": {
                      transition: `background-color ${transitionDuration}, color ${transitionDuration}`, // Transição para cabeçalho
                    },
                    "& td": {
                      transition: `background-color ${transitionDuration}, color ${transitionDuration}`, // Transição para células
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        onClick={() => handleSort("creationDate")}
                        sx={{
                          width: "20%", // 2 partes para Data de Criação
                          padding: "4px",
                          lineHeight: 1,
                          cursor: "pointer",
                          borderRight: "1px solid #ccc",
                          backgroundColor:
                            mode === "dark" ? "#3c3c3c" : "#90caf9",
                          color: mode === "dark" ? "#e0f6ff" : "#000000",
                          fontWeight: "bold",
                          fontSize: "1.0rem",
                        }}
                      >
                        <strong>
                          Data de Cadastro{renderSortArrow("creationDate")}
                        </strong>
                      </TableCell>
                      <TableCell
                        onClick={() => handleSort("type")}
                        sx={{
                          width: "20%", // 2 partes para Tipo
                          padding: "4px",
                          lineHeight: 1,
                          cursor: "pointer",
                          borderRight: "1px solid #ccc",
                          backgroundColor:
                            mode === "dark" ? "#3c3c3c" : "#90caf9",
                          color: mode === "dark" ? "#e0f6ff" : "#000000",
                          fontWeight: "bold",
                          fontSize: "1.0rem",
                        }}
                      >
                        <strong>Tipo {renderSortArrow("type")}</strong>
                      </TableCell>
                      <TableCell
                        onClick={() => handleSort("fullName")}
                        sx={{
                          width: "30%", // 3 partes para Nome Completo
                          padding: "4px",
                          lineHeight: 1,
                          cursor: "pointer",
                          borderRight: "1px solid #ccc",
                          backgroundColor:
                            mode === "dark" ? "#3c3c3c" : "#90caf9",
                          color: mode === "dark" ? "#e0f6ff" : "#000000",
                          fontWeight: "bold",
                          fontSize: "1.0rem",
                        }}
                      >
                        <strong>
                          Nome Completo {renderSortArrow("fullName")}
                        </strong>
                      </TableCell>
                      <TableCell
                        onClick={() => handleSort("login")}
                        sx={{
                          width: "20%", // 2 partes para o Login
                          padding: "4px",
                          lineHeight: 1,
                          cursor: "pointer",
                          borderRight: "1px solid #ccc",
                          backgroundColor:
                            mode === "dark" ? "#3c3c3c" : "#90caf9",
                          color: mode === "dark" ? "#e0f6ff" : "#000000",
                          fontWeight: "bold",
                          fontSize: "1.0rem",
                        }}
                      >
                        <strong>Login {renderSortArrow("login")}</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%", // 1 parte para Ações
                          padding: "4px",
                          lineHeight: 1,
                          backgroundColor:
                            mode === "dark" ? "#3c3c3c" : "#90caf9",
                          color: mode === "dark" ? "#e0f6ff" : "#000000",
                          fontWeight: "bold",
                          fontSize: "1.0rem",
                        }}
                      >
                        <strong>Ações</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ padding: "4px", lineHeight: 1 }}
                        >
                          Nenhum usuário encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow
                          key={user.id}
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                mode === "dark" ? "#555" : "#f5f5f5", // Cor de fundo ao passar o mouse
                              "& .delete-icon, & .edit-icon": {
                                visibility: "visible", // Torna os ícones visíveis quando passa o mouse na célula
                              },
                            },
                          }}
                        >
                          <TableCell
                            sx={{ width: "20%", padding: "4px", lineHeight: 1 }}
                          >
                            {new Date(user.creationDate).toLocaleDateString(
                              "pt-BR",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              }
                            )}{" "}
                            {new Date(user.creationDate).toLocaleTimeString(
                              "pt-BR",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ width: "20%", padding: "4px", lineHeight: 1 }}
                          >
                            {user.type === 99 ? "Administrador" : "Usuário"}
                          </TableCell>
                          <TableCell
                            sx={{ width: "30%", padding: "4px", lineHeight: 1 }}
                          >
                            {user.fullName}
                          </TableCell>
                          <TableCell
                            sx={{ width: "20%", padding: "4px", lineHeight: 1 }}
                          >
                            {user.login}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: "10%", // 1 parte para Ações
                              padding: "4px",
                              lineHeight: 1,
                              position: "relative", // Necessário para o posicionamento absoluto do ícone
                            }}
                          >
                            {/* Ícone de Editar Permissões */}
                            <Tooltip
                              title="Editar Permissões"
                              arrow
                              placement="left"
                            >
                              <FactCheckIcon
                                sx={{
                                  color: "#757575", // Cinza como cor inicial do ícone
                                  cursor: "pointer",
                                  position: "absolute",
                                  visibility: "hidden",
                                  top: "50%",
                                  left: "10%",
                                  transform: "translate(-50%, -50%)",
                                  transition: "color 0.3s ease", // Transição suave para hover
                                  "&:hover": { color: "#1565c0" }, // Azul ao passar o mouse
                                }}
                                className="edit-icon"
                                onClick={() => handleOpenPermissionsModal(user)} // Abre o modal ao clicar
                              />
                            </Tooltip>

                            {/* Ícone de Excluir */}
                            <Tooltip title="Excluir" arrow placement="right">
                              <DeleteForeverIcon
                                sx={{
                                  color: "#757575", // Cor inicial cinza (igual ao outro ícone)
                                  cursor:
                                    user.login === login || isDeleting
                                      ? "not-allowed"
                                      : "pointer",
                                  position: "absolute",
                                  visibility: "hidden", // Inicialmente escondido
                                  top: "50%",
                                  left: "30%",
                                  transform: "translate(-50%, -50%)",
                                  transition: "color 0.3s ease", // Transição suave para hover
                                  "&:hover": {
                                    color:
                                      user.login === login || isDeleting
                                        ? "#757575"
                                        : "#b71c1c", // Vermelho no hover, cinza se desativado
                                  },
                                }}
                                className="delete-icon"
                                onClick={() => {
                                  if (user.login !== login && !isDeleting) {
                                    handleOpenExcludeModal(user); // Abre o modal apenas se não for o usuário logado
                                  }
                                }}
                                onMouseEnter={() => {
                                  if (user.login === login) {
                                    setStatusMessage(
                                      "Você não pode excluir seu próprio usuário."
                                    );
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (user.login === login) {
                                    setStatusMessage(""); // Limpa a mensagem quando o cursor sai
                                  }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
                <Box
                  sx={{
                    marginTop: "auto", // Empurra o Box para o final
                    borderTop: `1px solid ${theme.palette.text.disabled}`,
                    textAlign: "left",
                    padding: "8px 4px",
                    backgroundColor: theme.palette.background.paper, // Fundo consistente
                    width: "100%", // Ocupa toda a largura
                    transition: "background-color 0.8s ease-in-out",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {statusMessage || " "}
                  </Typography>
                </Box>
          </Paper>
        </Box>
      </Box>
      <EditPermissionsModal
        open={permissionsModalOpen}
        onClose={handleClosePermissionsModal}
        user={permissionsUser} // Usuário selecionado
        loggedInUsername={login} // Usuário logado
      />

      {/* Modal de confirmação de exclusão */}
      <Dialog open={modalOpen} onClose={handleCloseModal}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza de que deseja excluir o usuário:{" "}
            <strong>
              {selectedUser} - {selectedUserName}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="primary" // Usa a cor definida no tema
          >
            Cancelar
          </Button>
          <Button
            onClick={handleExcludeUser}
            color="secondary" // Usa a cor de exclusão definida no tema
            disabled={isDeleting} // Desabilita o botão enquanto a requisição está em andamento
          >
            {isDeleting ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Exibir Toasts de Sucesso/Erro */}
      <ToastContainer position="top-right" autoClose={5000} />
    </ThemeProvider>
  );
};

export default Admin;
